import { FlexibleConnectedPositionStrategy, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { ComponentRef, ElementRef, inject, Injectable, InjectionToken, Injector, OnDestroy } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { WizardPanelCommentItemActionEvent } from '@wizard/wizard-panel/events';
import { map, merge, Observable, tap } from 'rxjs';
import { WizardPanelListItemEditorComponent } from './wizard-panel-list-item-editor.component';

export interface WizardPanelListItemEditorData {
  comment: string;
  mode: WizardPanelCommentItemActionEvent;
}

export const WIZARD_PANEL_LIST_ITEM_EDITOR_DATA = new InjectionToken<WizardPanelListItemEditorData>('WIZARD_PANEL_LIST_ITEM_EDITOR_DATA');

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class WizardPanelListItemEditorOverlayService implements OnDestroy {

  /** @Inject */
  readonly #overlay = inject(Overlay);

  #overlayRef: OverlayRef;
  #componentRef: ComponentRef<WizardPanelListItemEditorComponent>;

  constructor() { }

  ngOnDestroy() {
    this.dispose();
  }

  open(elementRef: ElementRef, data: WizardPanelListItemEditorData): Observable<WizardPanelListItemEditorData> {
    this.dispose();

    this.#overlayRef = this.#overlay.create({
      hasBackdrop: true,
      positionStrategy: this.getPositionStrategy(elementRef),
      scrollStrategy: this.#overlay.scrollStrategies.noop()
    });

    this.#componentRef = this.#overlayRef.attach(this.createComponentPortal(data));

    return merge(
      this.#overlayRef.backdropClick(),
      this.#componentRef.instance.close
    )
      .pipe(
        map(data => (data instanceof Event ? null : data)),
        tap(() => this.dispose())
      );
  }

  dispose() {
    this.#overlayRef && this.#overlayRef.dispose();
    this.#componentRef && this.#componentRef.destroy();
    this.#overlayRef = null;
    this.#componentRef = null;
  }

  private createComponentPortal(data: WizardPanelListItemEditorData): ComponentPortal<WizardPanelListItemEditorComponent> {
    return new ComponentPortal(
      WizardPanelListItemEditorComponent,
      null,
      Injector.create({
        parent: inject(Injector),
        providers: [
          {
            provide: WIZARD_PANEL_LIST_ITEM_EDITOR_DATA,
            useValue: data,
          },
        ],
      })
    );
  }

  private getPositionStrategy(elementRef: ElementRef): FlexibleConnectedPositionStrategy {
    return this.#overlay.position()
      .flexibleConnectedTo(elementRef)
      .withPositions([
        {
          originX: 'start',
          originY: 'bottom',
          overlayX: 'center',
          overlayY: 'top'
        }
      ]);
  }
}
